import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class DeepLinkService {
  private readonly baseUrl = '/flight/list';

  buildResultsUrl(searchData: any): string {
    const tripType = searchData.tripType;
    const formValue = searchData.formValue;
    const passengers = searchData.passengers;

    const trip = formValue.trips[0];

    const departureIata = trip.departureAirport.iata.toUpperCase();

    const arrivalIata = trip.arrivalAirport.iata.toUpperCase();

    const params = new URLSearchParams();

    params.set('adults', String(passengers.adult ?? 1));

    if (passengers.child > 0) {
      params.set('children', String(passengers.child));
    }

    if (passengers.infant > 0) {
      params.set('infants', String(passengers.infant));
    }

    params.set('cabin', formValue.classType.toLowerCase());
    if (formValue.maxNumberOfConnections !== null) {
      params.set('direct', String(formValue.maxNumberOfConnections));
    }

    switch (tripType) {
      case 'One Way':
        return (
          `${this.baseUrl}/oneway/` +
          `${departureIata}-${arrivalIata}/` +
          `${trip.departDate}?${params.toString()}`
        );

      case 'Round Trip':
        return (
          `${this.baseUrl}/round/` +
          `${departureIata}-${arrivalIata}/` +
          `${trip.departDate}/` +
          `${trip.returnDate}?${params.toString()}`
        );

      case 'Multi City': {
        const multiParams = new URLSearchParams();

        formValue.trips.forEach((segment: any) => {
          multiParams.append(
            'seg',
            `${segment.departureAirport.iata.toUpperCase()}-` +
              `${segment.arrivalAirport.iata.toUpperCase()}-` +
              `${segment.departDate}`
          );
        });

        multiParams.set('adults', String(passengers.adult ?? 1));

        if (passengers.child > 0) {
          multiParams.set('children', String(passengers.child));
        }

        if (passengers.infant > 0) {
          multiParams.set('infants', String(passengers.infant));
        }

        multiParams.set('cabin', formValue.classType.toLowerCase());
        if (formValue.maxNumberOfConnections !== null) {
          multiParams.set('direct', String(formValue.maxNumberOfConnections));
        }
        return `${this.baseUrl}/multi?${multiParams.toString()}`;
      }

      default:
        throw new Error('Unsupported trip type');
    }
  }

  parseResultsUrl(url: string): any {
    const parsedUrl = new URL(url, window.location.origin);

    const pathParts = parsedUrl.pathname.split('/').filter(Boolean);

    pathParts.forEach((part, index) => {
      const decoded = decodeURIComponent(part);

      if (!decoded.includes('?')) {
        return;
      }

      const [cleanPart, queryString] = decoded.split('?');

      pathParts[index] = cleanPart;

      const recoveredParams = new URLSearchParams(queryString);

      recoveredParams.forEach((value, key) => {
        if (!parsedUrl.searchParams.has(key)) {
          parsedUrl.searchParams.set(key, value);
        }
      });
    });

    if (
      pathParts.length < 3 ||
      pathParts[0] !== 'flight' ||
      pathParts[1] !== 'list'
    ) {
      throw new Error('Invalid deep link');
    }

    const searchParams = parsedUrl.searchParams;

    const cabin = (
      searchParams.get('cabin') ??
      searchParams.get('cabinclass') ??
      'economy'
    ).toUpperCase();

    const passengers = {
      adult: Number(searchParams.get('adults') ?? 1),
      child: Number(searchParams.get('children') ?? 0),
      infant: Number(searchParams.get('infants') ?? 0),
    };

    const result: any = {
      formValue: {
        classType: cabin,
        passenger: passengers,
        maxNumberOfConnections: searchParams.has('direct')
          ? Number(searchParams.get('direct'))
          : null,
        trips: [],
      },
      passengers,
      tripType: '',
    };

    const tripMode = pathParts[2];

    switch (tripMode) {
      case 'oneway': {
        const [origin, destination] = pathParts[3].split('-');

        result.tripType = 'One Way';

        result.formValue.trips.push({
          departureAirport: {
            iata: origin,
          },
          arrivalAirport: {
            iata: destination,
          },
          departDate: pathParts[4],
          returnDate: null,
        });

        break;
      }

      case 'round': {
        const [origin, destination] = pathParts[3].split('-');

        result.tripType = 'Round Trip';

        result.formValue.trips.push({
          departureAirport: {
            iata: origin,
          },
          arrivalAirport: {
            iata: destination,
          },
          departDate: pathParts[4],
          returnDate: pathParts[5],
        });

        break;
      }

      case 'multi': {
        result.tripType = 'Multi City';

        const segments = searchParams.getAll('seg');

        segments.forEach((seg) => {
          const pieces = seg.split('-');

          result.formValue.trips.push({
            departureAirport: {
              iata: pieces[0],
            },
            arrivalAirport: {
              iata: pieces[1],
            },
            departDate: `${pieces[2]}-${pieces[3]}-${pieces[4]}`,
            returnDate: null,
          });
        });

        break;
      }

      default:
        throw new Error(`Unknown trip type: ${tripMode}`);
    }

    return result;
  }
}
